import React, { useState, useCallback } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import type { Filter } from 'app-components/flux/filters/filtersTypes';
import type { PlannedSpendingItem } from 'app-components/flux/free-to-spend/freeToSpendTypes';
import { tracker } from 'app-components/utils/core';

import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { actionHelpers } from 'app-components/flux/core';
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { filtersActions, filtersSelectors } from 'app-components/flux/filters';
import { freeToSpendActions, freeToSpendSelectors, freeToSpendTypes } from 'app-components/flux/free-to-spend';
import type { FreeToSpend } from 'app-components/flux/free-to-spend/freeToSpendTypes';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { numberUtils } from 'app-components/utils/number';
import { deletePlannedSpendingDialog } from 'components/Dialogs/DeletePlannedSpending/actions';
import RolloverToggle from 'components/Dialogs/EditPlannedSpendingSeries/RolloverToggle';
import StdDialog from 'components/Dialogs/StdDialog';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import QIllustration from 'components/QIllustration';
import CategoryField from 'components/QuickenControls/CategoryField';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import usePlannedCategoryChecker from 'hooks/usePlannedCategoryChecker';
import type { CoaType } from 'hooks/usePlannedCategoryChecker';
import { DateTime } from 'luxon';
import { makeStyles } from 'tss-react/mui';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';

const useStyles = makeStyles()((theme) => ({
  iconCat: {
    height: 40,
    width: 0,
    visibility: 'hidden',
    filter: theme.palette.categoryIcon.filter,
    '&.visible': {
      paddingRight: 12,
      width: 40,
      visibility: 'visible',
    },
  },
  catAndAmt: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
  },
  spacer: {
    width: 24,
  },
  widthOfTextField: {
    margin: 0,
    width: '250px',
  },
  amountField: {
    margin: 0,
    textAlign: 'left',
  },
  dialogContent: {
    paddingTop: '16px !important',
  },
  suggested: {
    padding: theme.spacing(1),
    borderRadius: 12,
    width: '100%',
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    opacity: 0,
    marginBottom: theme.spacing(3),
  },
  show: {
    opacity: '1 !important',
  },
  delete: {
    color: theme.palette.amount.negative,
  },
  submitButton: {
    marginLeft: 8,
  },
}));

const defaultPlanned = Object.freeze({ name: '', targetAmount: 0, recurring: true }) as PlannedSpendingItem;
const pastCutoff = DateTime.local().startOf('month').minus({ months: 6 }).toSeconds();
const currCutoff = DateTime.local().startOf('month').minus({ months: 1 }).toSeconds();

function EditPlannedSpendingSeries({
  cardIndex,
  freeToSpendId,
  startDate,
  filterId,
  location,
}: {
  cardIndex: number;
  freeToSpendId: string;
  startDate: string;
  filterId: string;
  location: string;
}) {
  const [suggestedCategories, setSuggestedCategories] = usePreferenceV2('suggestedPSICategoryKnownIds');

  const futureRollovers = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanRollForward'),
  );
  const plannedItems = useAppSelector((state) =>
    freeToSpendSelectors.getPlannedSpendingItemsByF2SId(state, freeToSpendId),
  );
  const filter = useAppSelector((state) => filtersSelectors.getFilter(state, filterId));
  const freeToSpend = useAppSelector((state) => freeToSpendSelectors.getFreeToSpendById(state, freeToSpendId));
  const leftToSpend = useAppSelector((state) => freeToSpendSelectors.getLeftToSpend(state, freeToSpend as FreeToSpend));
  const calculatedPlannedAmount = useAppSelector((state) =>
    freeToSpendSelectors.getCalculatedPlannedSpending(state, freeToSpendId),
  );
  const parentCoaId = useAppSelector((state) => categoriesSelectors.getParentFromCoaList(state, filter));

  // Todays date formatted
  const todayDateUnformatted = useAppSelector(dateSelectors.getCurrentDateString);
  const todayDate = DateTime.fromISO(todayDateUnformatted).startOf('month').toISODate() || '';

  // This contains all the info on the planned item we will update rollovers for. We try to get todays' dates month, but if it doesn't exist, we grab the next one.
  const f2sRolloverUpdateItem = useAppSelector((state) =>
    freeToSpendSelectors.getEarliestPlannedSpendingItemNotInPastByFilterId(state, filterId),
  );
  const rolloverEditDate = futureRollovers && startDate >= todayDate ? startDate : f2sRolloverUpdateItem?.date;

  const rollover = useAppSelector((state) =>
    freeToSpendSelectors.getPlannedSpendingItemRolloverInfo(
      state,
      f2sRolloverUpdateItem?.selectedItem?.id,
      filterId,
      rolloverEditDate,
    ),
  );

  const plannedItem: PlannedSpendingItem = plannedItems?.get(cardIndex) || defaultPlanned;
  const { name } = plannedItem;
  const realTargetAmount = plannedItem.targetAmount;
  const [nameState, setName] = useState(name);
  const [targetAmountState, setTarget] = useState(plannedItem.targetAmount);

  const initResetRolloverAmount =
    typeof f2sRolloverUpdateItem?.selectedItem?.resetRolloverAmount === 'number'
      ? f2sRolloverUpdateItem?.selectedItem?.resetRolloverAmount
      : rollover;

  const initIsRolledOver = freeToSpendTypes.validRolloverStates.includes(
    f2sRolloverUpdateItem?.selectedItem?.rolloverType,
  );

  const [resetRolloverAmount, setResetRolloverAmount] = useState(initResetRolloverAmount);
  const [isRolledOver, setIsRolledOver] = useState(initIsRolledOver);

  // eslint-disable-next-line no-unused-vars
  const [_validate, coa, coaSetter, errorText, currCategory, category, _isAlreadyTracked, children] =
    usePlannedCategoryChecker({
      id: parentCoaId,
      startDate,
      isEdit: true,
    });

  // These are to track if the user changed the rollover amount on the updateItem
  const resetDidNotChange = !isRolledOver || initIsRolledOver || resetRolloverAmount === initResetRolloverAmount;
  const rolloversDidNotChange = rolloverEditDate ? isRolledOver === initIsRolledOver : true;

  const isDisabled =
    realTargetAmount === targetAmountState &&
    name === nameState &&
    coa?.id === category?.id &&
    rolloversDidNotChange &&
    resetDidNotChange;

  const isDisabledOrError = isDisabled || !!errorText || targetAmountState <= 0;
  const { averageSpending } = useAppSelector((state) =>
    freeToSpendSelectors.getAverageSpendingForCategory(state, { coaId: coa?.id, pastCutoff, currCutoff }),
  );

  const canUpdateRollover = rolloverEditDate;

  const { classes, cx } = useStyles();

  const dispatch = useAppDispatch();

  const removeCategoryFromSuggestedList = () => {
    if (suggestedCategories && setSuggestedCategories && suggestedCategories?.includes(currCategory?.knownCategoryId)) {
      setSuggestedCategories(suggestedCategories?.filter((item: string) => item !== currCategory?.knownCategoryId));
    }
  };

  const updateF2S = () => {
    const changed = [];
    if ((targetAmountState !== 0 && !targetAmountState) || targetAmountState < 0 || !coa.id) {
      return;
    }
    const item = plannedItems.get(cardIndex);
    const rolloverItem = f2sRolloverUpdateItem?.selectedItem;
    if (!item) {
      return;
    }
    if (nameState !== item.name) {
      changed.push('name');
      item.name = nameState;
      if (rolloverItem) rolloverItem.name = nameState; // We update the rollover item too because the selected item is ALWAYS either on the same date, or in the past.
    }
    if (coa?.id !== category?.id) {
      changed.push('category');
      dispatch(
        filtersActions.updateFilter({
          id: filterId,
          filterItems: [
            {
              type: 'CHARTOFACCOUNT',
              chartOfAccounts: [
                (coa as any)?.toJS?.() || coa, // TODO: Remove 'any' usage
                ...(children?.map(({ id }) => ({ id, type: 'CATEGORY' })) ?? []),
              ],
            },
          ],
        } as Filter),
      );
    }

    if (!rolloversDidNotChange) {
      tracker.track(tracker.events.rolloversOptIn, {
        initiator: 'edit',
        value: isRolledOver,
        category: currCategory?.name || 'Uncategorized',
      });

      if (isRolledOver) {
        tracker.track(tracker.events.startingRollover, {
          initiator: 'edit',
          sign: (resetRolloverAmount ?? 0) >= 0 ? 'positive' : 'negative',
          amount: resetRolloverAmount ?? 0,
        });
      }
    }

    // This is for editing the selected item the user clicked into!!
    if (rolloverEditDate === startDate && canUpdateRollover) {
      item.rolloverType = isRolledOver
        ? freeToSpendTypes.RolloverTypeEnum.BOTH
        : freeToSpendTypes.RolloverTypeEnum.NONE;

      if (rolloversDidNotChange) {
        // If the user did not change rollover state
        if (isRolledOver && !resetDidNotChange && typeof resetRolloverAmount === 'number') {
          // And the user also changed the reset amount
          item.resetRolloverAmount = resetRolloverAmount;
        }
      } else if (isRolledOver && typeof resetRolloverAmount === 'number') {
        item.resetRolloverAmount = resetRolloverAmount;
      }
    }

    if (targetAmountState !== realTargetAmount) {
      changed.push('amount');
      // reset the overwritten target amount when user edits the series
      if (item.overwrittenTargetAmount || item.overwrittenTargetAmount === 0) {
        // The property does not exist in the type because it is a value only QCS uses
        // eslint-disable-next-line
        (item as any).resetOverwrittenTargetAmount = true; // TODO: Remove 'any' usage
        if (item.overwrittenTargetAmount === 0 || item.overwrittenTargetAmount) delete item.overwrittenTargetAmount;
      }
      item.targetAmount = targetAmountState;
      if (rolloverItem) rolloverItem.targetAmount = targetAmountState;
    }

    const dateObj = DateTime.fromISO(startDate);

    tracker.track(tracker.events.budgetItemEdit, {
      month: dateObj.month,
      year: dateObj.year,
      full_date: startDate,
      location,
      fields: changed,
    });

    const spendingItems = plannedItems.splice(cardIndex, 1, item); // TODO: change this to rely on first finding the item

    let total = 0;
    spendingItems?.forEach((plannedValue) => {
      const calculatedSpentAmount = plannedValue?.calculatedSpentAmount ?? 0;
      const overwrittenTargetAmount = plannedValue?.overwrittenTargetAmount ?? 0;
      const targetAmount = plannedValue?.targetAmount ?? 0;
      const spentAmount = -1 * calculatedSpentAmount;

      if ((overwrittenTargetAmount === 0 || overwrittenTargetAmount) && overwrittenTargetAmount >= spentAmount) {
        total += overwrittenTargetAmount;
      } else if (targetAmount > spentAmount) {
        total += targetAmount;
      } else {
        total += spentAmount;
      }
    });
    const leftToSpendAmount = Number(leftToSpend) - Number(calculatedPlannedAmount) - total;

    // We update the selected item first!
    actionHelpers
      .bindPromiseAction(
        dispatch,
        freeToSpendActions.updateFreeToSpend,
      )({
        id: freeToSpendId,
        plannedSpendingItems: spendingItems,
        calculatedPlannedSpendingAmount: -1 * total,
        leftToSpendAmount,
      })
      .then((_resp) => {
        const f2sObject = f2sRolloverUpdateItem?.f2sObject;

        // This is for editing the current date item if rollovers require it!!
        if (
          rolloverItem &&
          canUpdateRollover &&
          rolloverEditDate !== startDate &&
          (!resetDidNotChange || !rolloversDidNotChange)
        ) {
          rolloverItem.rolloverType = isRolledOver
            ? freeToSpendTypes.RolloverTypeEnum.BOTH
            : freeToSpendTypes.RolloverTypeEnum.NONE;

          if (rolloversDidNotChange) {
            // If the user did not change rollover state
            if (resetRolloverAmount && isRolledOver && !resetDidNotChange) {
              // And the user also changed the reset amount
              rolloverItem.resetRolloverAmount = resetRolloverAmount;
            }
          } else if (isRolledOver && resetRolloverAmount) {
            rolloverItem.resetRolloverAmount = resetRolloverAmount;
          }

          // Go through the items and only edit the one we want to edit in the f2sObject!!
          const newSpendingItems = f2sObject?.plannedSpendingItems?.map((myItem: PlannedSpendingItem) => {
            if (myItem.filterId === rolloverItem.filterId) {
              return rolloverItem;
            }
            return myItem;
          });
          if (newSpendingItems && f2sObject?.id) {
            dispatch(
              freeToSpendActions.updateFreeToSpend({ id: f2sObject?.id, plannedSpendingItems: newSpendingItems }),
            );
          }
        }
        removeCategoryFromSuggestedList();
      })
      .catch((error) => assert(false, error));
  };

  const setPlannedName = useCallback((e: any) => {
    setName(e.target.value);
  }, []);

  const onHandleClose = (event: any, reason: string) => {
    if (typeof event?.stopPropagation === 'function') {
      event?.stopPropagation();
    }
    if (typeof event?.preventDefault === 'function') {
      event?.preventDefault();
    }
    if (reason === 'SUBMIT_BUTTON' || reason === 'ENTER_KEY_DOWN') {
      // update
      updateF2S();
    }
    dispatch(rootUiActions.removeDialog());

    // editHide();
  };

  const filterFn = (c: CoaType) =>
    c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE ||
    (c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isExpenseCat(null, c?.id ?? '')) ||
    c.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED; // only expense categories or uncat

  const formatAmount = useFormatAmount();
  const formatNumber = (value: number) =>
    formatAmount(Math.abs(value || 0), 'USD', '0,0.00', numberUtils.ShowSignEnum.NEGATIVE_ONLY);

  const showAverageSpend = coa.id && Math.abs(averageSpending || 0) > 0;

  return (
    <StdDialog
      onClose={onHandleClose}
      onKeyDown={(e: any) => {
        if (e.code === 'Enter' && !isDisabledOrError) {
          onHandleClose(e, 'ENTER_KEY_DOWN');
        }
      }}
      open
      showCloseButton
      size={'md'}
      title={'Edit expense series'}
    >
      <DialogContent className={classes.dialogContent}>
        <Box display={'flex'}>
          <QIllustration name='spendingPlanPlanned' size='md' />
          <Box padding={2} paddingTop={2} maxWidth={'500px'}>
            <Stack spacing={3} width={'100%'}>
              <Typography variant='bodyRegular' component={'span'}>
                {'Any changes will be made to all future months.'}
              </Typography>
              <div className={classes.catAndAmt}>
                <TextField
                  margin='normal'
                  id='name'
                  label={'Name (optional)'}
                  inputProps={{ maxLength: 30 }}
                  variant='filled'
                  value={nameState}
                  onChange={setPlannedName}
                  className={classes.widthOfTextField}
                />
                {<div className={classes.spacer} />}
                <Box flexDirection={'column'}>
                  <QAmountField
                    editable
                    id='target-amount'
                    variant={'filled'}
                    label={'Target Amount'}
                    value={targetAmountState}
                    onChange={(e: any) => setTarget(Number(e.target.value))}
                    textFieldVariant={'filled'}
                    className={classes.amountField}
                    error={targetAmountState < 0}
                    expectedSign='positive'
                    margin='none'
                  />
                </Box>
              </div>
              <div className={classes.catAndAmt}>
                <Box flexDirection={'column'} width={'100%'}>
                  <CategoryField
                    variant='bodyRegular'
                    id='monthly'
                    editable
                    textFieldVariant='filled'
                    filterFn={filterFn}
                    value={coa}
                    onChange={(c: CoaType) => coaSetter(c, plannedItem)}
                    width={'100%'}
                    margin='none'
                    error={!!errorText}
                    helperText={errorText}
                    label='Select category'
                    textFieldProps={{
                      label: 'Category',
                      variant: 'filled',
                      margin: 'none',
                    }}
                  />
                </Box>
              </div>
              <Box flexDirection={'column'} display={'flex'} justifyContent={'space-between'} width={'100%'}>
                <div className={cx(classes.suggested, showAverageSpend && classes.show)}>
                  <img
                    alt='cat-icon'
                    src={currCategory?.logoUrl || ''}
                    className={cx(classes.iconCat, currCategory?.logoUrl ? 'visible' : '')}
                  />
                  <Typography variant='paragraphRegular'>
                    {`Average spend in this category each month: ${formatNumber(averageSpending)}`}
                  </Typography>
                </div>
                {canUpdateRollover ? (
                  <RolloverToggle
                    initRolloverSetting={initIsRolledOver}
                    isRolledOver={isRolledOver}
                    setIsRolledOver={setIsRolledOver}
                    startDate={rolloverEditDate}
                    resetRolloverAmount={resetRolloverAmount}
                    setResetRolloverAmount={setResetRolloverAmount}
                  />
                ) : null}
              </Box>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display={'flex'} justifyContent={'space-between'} width={'100%'}>
          <Box />
          <Box>
            <QButton
              className={classes.delete}
              type='submit'
              id={'edit-delete-button'}
              onClick={() => {
                dispatch(rootUiActions.removeDialog());
                dispatch(deletePlannedSpendingDialog(plannedItem.filterId, freeToSpendId, '', true));
              }}
            >
              Delete series
            </QButton>
            <QButton
              variant='contained'
              type='submit'
              id={'update-button'}
              onClick={(e: any) => {
                onHandleClose(e, 'SUBMIT_BUTTON');
              }}
              disabled={isDisabledOrError}
              className={classes.submitButton}
            >
              UPDATE
            </QButton>
          </Box>
        </Box>
      </DialogActions>
    </StdDialog>
  );
}

export default React.memo(EditPlannedSpendingSeries);
